import {useMemo} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";

import {isEmpty, sum} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../app/rp_reducer";
import {IRegions, PropertiesRoomsAvailableType} from "../atoms/LocationLinks";
import {getCheapOffersCount, getLuxuryOfferCount, getReadyOfferCount} from "../offer/atoms/OfferLocationLinksOtherOffers";
import {offerUrlBuilder} from "../offer/helpers/friendly_offer_list/offer_url_builder";
import {getOfferTypeEnglishNamePlural} from "../offer/helpers/OfferType";
import {OfferType} from "../offer/types/OfferType";
import {getRegionDataForVendorListing} from "../offer/utils/get_region_data_for_vendor_listing";
import {buildFriendlyPromotionSlug} from "../promotion/helpers/build_friendly_promotion_slug";
import {FloorChoice} from "../real_estate/types/FloorChoice";
import {RegionType} from "../region/types/RegionType";
import {PRICES_CITIES_SLUGS} from "../report/price/constants/prices_cities";
import {ILatestQuery} from "../utils/latest_query";
import {ViewType} from "../view_type/ViewType";

type UseLocationLinkingType = {
    otherOffersLink: string[];
    regionAvailableRoomsLink: {hasLinks: boolean};
    availableFloorTypes: FloorChoice[];
};

export const useLocationLinksCheck = (
    lastQuery: ILatestQuery & Record<string, string | string[] | null>,
    offerType: OfferType | null,
    viewType: ViewType | null,
    region?: IRegions
): UseLocationLinkingType => {
    const listingIsCheap = lastQuery.price_lower_than_average;
    const isVoivodeship = region?.type === RegionType.VOIVODESHIP;
    const {pathname} = useLocation();

    const cheapOffersCountForSale = region?.stats && getCheapOffersCount(offerType, region.stats);
    const isCity = region && [RegionType.CITY, RegionType.TOWN].includes(region.type);
    const isDistrict = region?.type === RegionType.DISTRICT;
    const listingIsReady = lastQuery.construction_end_date === "0";
    const readyCountForSale = region?.stats
        ? getReadyOfferCount(
              offerType,
              region.stats.properties_houses_count_ready,
              region.stats.properties_flats_count_ready,
              region.stats.properties_commercial_count_ready
          )
        : 0;
    const promotionLink = region && rpAppLink.promotions.listFriendly({friendlySlug: buildFriendlyPromotionSlug({region: region.slug})});
    const search = useSelector((store: IRPStore) => store.offerList.search);
    const listingIsLuxury = !!search && search.is_luxury;
    const luxuryCountForSale = region?.stats ? getLuxuryOfferCount(offerType, region.stats) : 0;
    const selectedRegions = useSelector((state: IRPStore) => state.offerList.selectedRegions);

    const vendorsRegionTypes = [RegionType.VOIVODESHIP, RegionType.CITY, RegionType.NEIGHBORHOOD, RegionType.DISTRICT];
    const showVendorsLink =
        (selectedRegions.length === 1 && vendorsRegionTypes.includes(selectedRegions[0].type)) || (region && vendorsRegionTypes.includes(region.type));
    const parentRegions = useSelector((state: IRPStore) => state.offerList.regionPath);

    const regionDataForVendorListing = region && getRegionDataForVendorListing(region, parentRegions);

    const roomsRecords =
        region &&
        region.stats &&
        region.stats[`properties_${offerType ? getOfferTypeEnglishNamePlural(offerType) : "all"}_rooms_ranges` as PropertiesRoomsAvailableType];

    const priceReportExist =
        region &&
        (PRICES_CITIES_SLUGS.includes(region.slug) || (region.stats?.region_type_city && PRICES_CITIES_SLUGS.includes(region.stats.region_type_city.slug)));

    const oneToFiveRoomsRanges = useMemo(
        () =>
            Object.entries(roomsRecords || {}).reduce((acc: Record<number, number>, [key, value]) => {
                if (parseInt(key, 10) > 5) {
                    return {
                        ...acc,
                        5: acc[5] + value
                    };
                }
                return {...acc, [key]: value};
            }, {}),
        [roomsRecords]
    );
    const houseStats = {
        properties_houses_serial_count: region?.stats?.properties_houses_serial_count || 0,
        properties_houses_detached_count: region?.stats?.properties_houses_detached_count || 0,
        properties_houses_semi_detached_count: region?.stats?.properties_houses_semi_detached_count || 0,
        properties_houses_additional_areas_attic: region?.stats?.properties_houses_additional_areas_attic || 0
    };
    const houseOffersInRegion = useMemo(() => {
        if (!region || !region.stats) {
            return 0;
        }

        return sum(Object.values(houseStats));
    }, [region]);

    const availableFloorTypes = useMemo(() => {
        const type = offerType === OfferType.FLAT ? "flats" : null;

        if (!type) {
            return [];
        }

        const lastFloor = region?.stats?.[`properties_${type}_last_floor`];
        const groundFloorWithGarden = region?.stats?.[`properties_${type}_ground_floor_with_garden`];
        const groundFloor = region?.stats?.[`properties_${type}_ground_floor`];

        const floorTypes = [];

        if (lastFloor) {
            floorTypes.push(FloorChoice.LAST_LEVEL);
        }

        if (groundFloorWithGarden) {
            floorTypes.push(FloorChoice.GROUND_WITH_GARDEN);
        }

        if (groundFloor) {
            floorTypes.push(FloorChoice.GROUND);
        }

        return floorTypes;
    }, [offerType, region]);

    const otherOffersLink = useMemo(() => {
        const results: string[] = [];

        if (
            !listingIsCheap &&
            ((cheapOffersCountForSale && cheapOffersCountForSale > 0) || isVoivodeship) &&
            offerType !== OfferType.COMMERCIAL &&
            (isCity || (isDistrict && offerType === OfferType.FLAT) || (isVoivodeship && offerType === OfferType.HOUSE))
        ) {
            results.push("cheap");
        }

        if (
            !listingIsReady &&
            readyCountForSale > 0 &&
            region &&
            [RegionType.CITY, RegionType.TOWN].includes(region.type) &&
            offerType &&
            offerType !== OfferType.COMMERCIAL
        ) {
            results.push("ready");
        }

        if (priceReportExist) {
            results.push("price");
        }

        if (promotionLink && region.stats && region.stats.promotions_count_total > 0) {
            results.push("promotion");
        }

        if (!listingIsLuxury && offerType && offerType !== OfferType.COMMERCIAL && luxuryCountForSale > 0 && isCity) {
            results.push("luxury");
        }

        if (showVendorsLink && regionDataForVendorListing) {
            results.push("vendors");
        }

        return results;
    }, [
        listingIsCheap,
        cheapOffersCountForSale,
        isVoivodeship,
        offerType,
        isCity,
        isDistrict,
        listingIsReady,
        readyCountForSale,
        region,
        promotionLink,
        region?.stats,
        listingIsLuxury,
        luxuryCountForSale,
        showVendorsLink,
        regionDataForVendorListing,
        priceReportExist
    ]);

    const regionAvailableRoomsLink = useMemo(() => {
        if (!isEmpty(oneToFiveRoomsRanges) || houseOffersInRegion > 0 || offerType === OfferType.FLAT) {
            if (offerType === OfferType.HOUSE && region?.stats?.properties_houses_count_for_sale === 0) {
                return {hasLinks: false};
            }
            if (offerType === OfferType.FLAT && region?.stats?.properties_flats_count_for_sale === 0) {
                return {hasLinks: false};
            }

            const checkSelfLinking = Object.entries(oneToFiveRoomsRanges)
                .map(([roomsStr]) => {
                    const rooms = parseInt(roomsStr, 10);
                    const regionFriendlySlug = offerUrlBuilder({
                        type: offerType,
                        region: region?.slug,
                        rooms_0: rooms,
                        rooms_1: rooms
                    }).friendlySlug;
                    const link = rpAppLink.offer.listFriendly({friendlySlug: regionFriendlySlug});
                    return link !== pathname;
                })
                .some((value) => value === true);
            if (!checkSelfLinking) {
                return {hasLinks: false};
            }

            return {hasLinks: true};
        }
        return {hasLinks: false};
    }, [oneToFiveRoomsRanges, houseOffersInRegion, offerType, region?.slug, pathname]);

    return {otherOffersLink, regionAvailableRoomsLink, availableFloorTypes};
};
import {useMemo} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";

import {isEmpty, sum} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../app/rp_reducer";
import {IRegions, PropertiesRoomsAvailableType} from "../atoms/LocationLinks";
import {getCheapOffersCount, getLuxuryOfferCount, getReadyOfferCount} from "../offer/atoms/OfferLocationLinksOtherOffers";
import {offerUrlBuilder} from "../offer/helpers/friendly_offer_list/offer_url_builder";
import {getOfferTypeEnglishNamePlural} from "../offer/helpers/OfferType";
import {OfferType} from "../offer/types/OfferType";
import {getRegionDataForVendorListing} from "../offer/utils/get_region_data_for_vendor_listing";
import {buildFriendlyPromotionSlug} from "../promotion/helpers/build_friendly_promotion_slug";
import {FloorChoice} from "../real_estate/types/FloorChoice";
import {RegionType} from "../region/types/RegionType";
import {PRICES_CITIES_SLUGS} from "../report/price/constants/prices_cities";
import {ILatestQuery} from "../utils/latest_query";
import {ViewType} from "../view_type/ViewType";

type UseLocationLinkingType = {
    otherOffersLink: string[];
    regionAvailableRoomsLink: {hasLinks: boolean};
    availableFloorTypes: FloorChoice[];
};

export const useLocationLinksCheck = (
    lastQuery: ILatestQuery & Record<string, string | string[] | null>,
    offerType: OfferType | null,
    viewType: ViewType | null,
    region?: IRegions
): UseLocationLinkingType => {
    const listingIsCheap = lastQuery.price_lower_than_average;
    const isVoivodeship = region?.type === RegionType.VOIVODESHIP;
    const {pathname} = useLocation();

    const cheapOffersCountForSale = region?.stats && getCheapOffersCount(offerType, region.stats);
    const isCity = region && [RegionType.CITY, RegionType.TOWN].includes(region.type);
    const isDistrict = region?.type === RegionType.DISTRICT;
    const listingIsReady = lastQuery.construction_end_date === "0";
    const readyCountForSale = region?.stats
        ? getReadyOfferCount(
              offerType,
              region.stats.properties_houses_count_ready,
              region.stats.properties_flats_count_ready,
              region.stats.properties_commercial_count_ready
          )
        : 0;
    const promotionLink = region && rpAppLink.promotions.listFriendly({friendlySlug: buildFriendlyPromotionSlug({region: region.slug})});
    const search = useSelector((store: IRPStore) => store.offerList.search);
    const listingIsLuxury = !!search && search.is_luxury;
    const luxuryCountForSale = region?.stats ? getLuxuryOfferCount(offerType, region.stats) : 0;
    const selectedRegions = useSelector((state: IRPStore) => state.offerList.selectedRegions);

    const vendorsRegionTypes = [RegionType.VOIVODESHIP, RegionType.CITY, RegionType.NEIGHBORHOOD, RegionType.DISTRICT];
    const showVendorsLink =
        (selectedRegions.length === 1 && vendorsRegionTypes.includes(selectedRegions[0].type)) || (region && vendorsRegionTypes.includes(region.type));
    const parentRegions = useSelector((state: IRPStore) => state.offerList.regionPath);

    const regionDataForVendorListing = region && getRegionDataForVendorListing(region, parentRegions);

    const roomsRecords =
        region &&
        region.stats &&
        region.stats[`properties_${offerType ? getOfferTypeEnglishNamePlural(offerType) : "all"}_rooms_ranges` as PropertiesRoomsAvailableType];

    const priceReportExist =
        region &&
        (PRICES_CITIES_SLUGS.includes(region.slug) || (region.stats?.region_type_city && PRICES_CITIES_SLUGS.includes(region.stats.region_type_city.slug)));

    const oneToFiveRoomsRanges = useMemo(
        () =>
            Object.entries(roomsRecords || {}).reduce((acc: Record<number, number>, [key, value]) => {
                if (parseInt(key, 10) > 5) {
                    return {
                        ...acc,
                        5: acc[5] + value
                    };
                }
                return {...acc, [key]: value};
            }, {}),
        [roomsRecords]
    );
    const houseStats = {
        properties_houses_serial_count: region?.stats?.properties_houses_serial_count || 0,
        properties_houses_detached_count: region?.stats?.properties_houses_detached_count || 0,
        properties_houses_semi_detached_count: region?.stats?.properties_houses_semi_detached_count || 0,
        properties_houses_additional_areas_attic: region?.stats?.properties_houses_additional_areas_attic || 0
    };
    const houseOffersInRegion = useMemo(() => {
        if (!region || !region.stats) {
            return 0;
        }

        return sum(Object.values(houseStats));
    }, [region]);

    const availableFloorTypes = useMemo(() => {
        const type = offerType === OfferType.FLAT ? "flats" : null;

        if (!type) {
            return [];
        }

        const lastFloor = region?.stats?.[`properties_${type}_last_floor`];
        const groundFloorWithGarden = region?.stats?.[`properties_${type}_ground_floor_with_garden`];
        const groundFloor = region?.stats?.[`properties_${type}_ground_floor`];

        const floorTypes = [];

        if (lastFloor) {
            floorTypes.push(FloorChoice.LAST_LEVEL);
        }

        if (groundFloorWithGarden) {
            floorTypes.push(FloorChoice.GROUND_WITH_GARDEN);
        }

        if (groundFloor) {
            floorTypes.push(FloorChoice.GROUND);
        }

        return floorTypes;
    }, [offerType, region]);

    const otherOffersLink = useMemo(() => {
        const results: string[] = [];

        if (
            !listingIsCheap &&
            ((cheapOffersCountForSale && cheapOffersCountForSale > 0) || isVoivodeship) &&
            offerType !== OfferType.COMMERCIAL &&
            (isCity || (isDistrict && offerType === OfferType.FLAT) || (isVoivodeship && offerType === OfferType.HOUSE))
        ) {
            results.push("cheap");
        }

        if (
            !listingIsReady &&
            readyCountForSale > 0 &&
            region &&
            [RegionType.CITY, RegionType.TOWN].includes(region.type) &&
            offerType &&
            offerType !== OfferType.COMMERCIAL
        ) {
            results.push("ready");
        }

        if (priceReportExist) {
            results.push("price");
        }

        if (promotionLink && region.stats && region.stats.promotions_count_total > 0) {
            results.push("promotion");
        }

        if (!listingIsLuxury && offerType && offerType !== OfferType.COMMERCIAL && luxuryCountForSale > 0 && isCity) {
            results.push("luxury");
        }

        if (showVendorsLink && regionDataForVendorListing) {
            results.push("vendors");
        }

        return results;
    }, [
        listingIsCheap,
        cheapOffersCountForSale,
        isVoivodeship,
        offerType,
        isCity,
        isDistrict,
        listingIsReady,
        readyCountForSale,
        region,
        promotionLink,
        region?.stats,
        listingIsLuxury,
        luxuryCountForSale,
        showVendorsLink,
        regionDataForVendorListing,
        priceReportExist
    ]);

    const regionAvailableRoomsLink = useMemo(() => {
        if (!isEmpty(oneToFiveRoomsRanges) || houseOffersInRegion > 0 || offerType === OfferType.FLAT) {
            if (offerType === OfferType.HOUSE && region?.stats?.properties_houses_count_for_sale === 0) {
                return {hasLinks: false};
            }
            if (offerType === OfferType.FLAT && region?.stats?.properties_flats_count_for_sale === 0) {
                return {hasLinks: false};
            }

            const checkSelfLinking = Object.entries(oneToFiveRoomsRanges)
                .map(([roomsStr]) => {
                    const rooms = parseInt(roomsStr, 10);
                    const regionFriendlySlug = offerUrlBuilder({
                        type: offerType,
                        region: region?.slug,
                        rooms_0: rooms,
                        rooms_1: rooms
                    }).friendlySlug;
                    const link = rpAppLink.offer.listFriendly({friendlySlug: regionFriendlySlug});
                    return link !== pathname;
                })
                .some((value) => value === true);
            if (!checkSelfLinking) {
                return {hasLinks: false};
            }

            return {hasLinks: true};
        }
        return {hasLinks: false};
    }, [oneToFiveRoomsRanges, houseOffersInRegion, offerType, region?.slug, pathname]);

    return {otherOffersLink, regionAvailableRoomsLink, availableFloorTypes};
};
